import React, { useEffect, useState } from 'react';

import clinicalNoteService from './clinicalNoteService.js';
import './ClinicalNotePlanOfCareGate.css';
import ClinicalNoteCollapsibleSection from './ClinicalNoteCollapsibleSection.jsx';
import ClinicalNotePlanOfCareSection from './ClinicalNotePlanOfCareSection.jsx';

/**
 * Payer-aware Plan of Care presentation gate.
 *
 * A payer-required Plan of Care opens its editor as before. If it is not
 * required, an already-persisted Plan of Care is still accessible, while a
 * new optional one can be opened only by the clinician's explicit action.
 * Opening the optional editor alone does not create a record: persistence
 * belongs to ClinicalNotePlanOfCareSection and its backend save endpoint.
 *
 * The clinician's documentation choice and the payer's certification
 * requirement are separate. The backend controls final validation and PDF.
 */
export default function ClinicalNotePlanOfCareGate({
  clinicalNoteId,
  disabled = false,
  onSaved,
  onVersionChange,
  navigationCoordinator = null,
  status,
  accordionGroup = 'clinical-note-sections',
  sectionKey,
  previousSectionKey = null,
  nextSectionKey = null,
}) {
  const [required, setRequired] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [optionalOpen, setOptionalOpen] = useState(false);
  const [hasExistingOptionalPlan, setHasExistingOptionalPlan] = useState(false);
  const [optionalCheckError, setOptionalCheckError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadRequirement() {
      setRequired(null);
      setLoadError('');
      setOptionalOpen(false);
      setHasExistingOptionalPlan(false);
      setOptionalCheckError('');

      try {
        const payerRequired = Boolean(
          await clinicalNoteService.isPlanOfCareRequired(clinicalNoteId)
        );
        if (cancelled) return;
        setRequired(payerRequired);

        if (!payerRequired) {
          try {
            // getPlanOfCare may return an unsaved template preview. Only a
            // real persisted ID means optional documentation already exists.
            const existing = await clinicalNoteService.getPlanOfCare(clinicalNoteId);
            if (cancelled) return;
            const persisted = existing?.planOfCareId != null;
            setHasExistingOptionalPlan(persisted);
            setOptionalOpen(persisted);
          } catch (error) {
            if (!cancelled) {
              setOptionalCheckError(
                error?.message || 'Could not check for an existing optional Plan of Care.'
              );
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(
            error?.message || 'Unable to determine whether a Plan of Care is required.'
          );
        }
      }
    }

    if (clinicalNoteId) {
      void loadRequirement();
    } else {
      setRequired(null);
      setLoadError('Clinical note ID is required.');
    }

    return () => {
      cancelled = true;
    };
  }, [clinicalNoteId]);

  const resolvedSectionKey = sectionKey || `${clinicalNoteId}-plan-of-care`;
  const accordionProps = {
    defaultCollapsed: true,
    resetKey: resolvedSectionKey,
    accordionGroup,
    sectionKey: resolvedSectionKey,
    previousSectionKey,
    nextSectionKey,
  };

  if (loadError) {
    return (
      <ClinicalNoteCollapsibleSection
        title="Plan of Care"
        status={{ level: 'red', label: 'Requirement error' }}
        {...accordionProps}
      >
        <section role="alert" className="clinical-poc-gate-message clinical-poc-gate-error">
          <strong>Plan of Care requirement could not be loaded</strong>
          <div>{loadError}</div>
        </section>
      </ClinicalNoteCollapsibleSection>
    );
  }

  if (required === null) {
    return (
      <ClinicalNoteCollapsibleSection
        title="Plan of Care"
        status={{ level: 'neutral', label: 'Checking…' }}
        {...accordionProps}
      >
        <section className="clinical-poc-gate-message">
          Checking payer Plan of Care requirements…
        </section>
      </ClinicalNoteCollapsibleSection>
    );
  }

  const showEditor = required || optionalOpen;
  return (
    <ClinicalNoteCollapsibleSection
      title={required ? 'Plan of Care' : 'Plan of Care — Optional'}
      status={
        required
          ? status
          : showEditor && hasExistingOptionalPlan
            ? (status || { level: 'neutral', label: 'Optional documentation' })
            : { level: 'neutral', label: showEditor ? 'Optional editor' : 'Not required' }
      }
      {...accordionProps}
    >
      {!required && (
        <div className="clinical-poc-gate-message">
          <div>
            This payer does not require a Plan of Care for this note.
            A therapist may still document an optional Plan of Care when clinically appropriate.
            Opening the editor does not automatically save or certify one.
          </div>
          {optionalCheckError && (
            <p role="status" className="clinical-poc-gate-caution">
              Existing documentation could not be verified: {optionalCheckError}
            </p>
          )}
          {!optionalOpen && (
            <button
              type="button"
              className="clinical-poc-gate-button"
              onClick={() => setOptionalOpen(true)}
            >
              {disabled ? 'View Optional Plan of Care' : 'Open Optional Plan of Care'}
            </button>
          )}
        </div>
      )}

      {showEditor && (
        <ClinicalNotePlanOfCareSection
          clinicalNoteId={clinicalNoteId}
          disabled={disabled}
          onSaved={onSaved}
          onVersionChange={onVersionChange}
          navigationCoordinator={navigationCoordinator}
        />
      )}
    </ClinicalNoteCollapsibleSection>
  );
}
